/**
 * Desktop services mega menu component
 */

import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { 
  getServicesGrouped, 
  GROUP_LABELS, 
  type ServiceGroup, 
  type ServiceMenuItem 
} from '@/config/servicesMenu';
import { trackMenuClick, trackMenuToggle } from '@/analytics/menu';
import CityQuickLinks from './CityQuickLinks';
import type { SwissLanguage } from '@/utils/languageTypes';
import { 
  Video, Film, Bot, Box, Wand2, Headphones, Monitor, Search, 
  Megaphone, MapPin, PenTool, Cpu, Mail, Palette, ShoppingCart, 
  Glasses, Music2 
} from 'lucide-react';

// Icon mapping for services
const iconMap: Record<string, React.ComponentType<any>> = {
  video: Video,
  film: Film,
  bot: Bot,
  cube: Box,
  wand2: Wand2,
  headphones: Headphones,
  monitor: Monitor,
  search: Search,
  megaphone: Megaphone,
  'map-pin': MapPin,
  'pen-tool': PenTool,
  cpu: Cpu,
  mail: Mail,
  palette: Palette,
  'shopping-cart': ShoppingCart,
  vr: Glasses,
  music2: Music2,
};

interface DesktopServicesMegaMenuProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

const DesktopServicesMegaMenu: React.FC<DesktopServicesMegaMenuProps> = ({ 
  isOpen, 
  onToggle 
}) => {
  const { currentLanguage } = useLanguage();
  const [hoveredService, setHoveredService] = useState<ServiceMenuItem | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const groupedServices = getServicesGrouped();
  const locale = currentLanguage as SwissLanguage;

  // Handle mouse enter/leave with delay
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!isOpen) {
      onToggle(true);
      trackMenuToggle('open', 'header');
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      onToggle(false);
      setHoveredService(null);
      trackMenuToggle('close', 'header');
    }, 150);
  };

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onToggle(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onToggle(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const handleServiceClick = (service: ServiceMenuItem) => {
    trackMenuClick({
      location: 'header',
      service_id: service.id,
      locale,
      group: service.group,
      has_children: !!service.children
    });
    onToggle(false);
  };

  const handleServiceHover = (service: ServiceMenuItem) => {
    setHoveredService(service);
  };

  const ServiceIcon: React.FC<{ iconName?: string }> = ({ iconName }) => {
    if (!iconName || !iconMap[iconName]) return null;
    
    const Icon = iconMap[iconName];
    return <Icon className="w-4 h-4" />;
  };

  const translations = {
    de: { 
      bundlePromo: 'Launch Bundle – 14 Tage live',
      bundlePrice: 'ab CHF 6,900'
    },
    en: { 
      bundlePromo: 'Launch Bundle – Live in 14 days',
      bundlePrice: 'from CHF 6,900'
    },
    fr: { 
      bundlePromo: 'Bundle Launch – En ligne en 14 jours',
      bundlePrice: 'à partir de CHF 6,900'
    },
    it: { 
      bundlePromo: 'Bundle Launch – Online in 14 giorni',
      bundlePrice: 'da CHF 6,900'
    }
  };

  const t = translations[locale];

  return (
    <div 
      ref={menuRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Services Button */}
      <button
        className={`text-gray-100 hover:text-purple-400 transition-colors font-future text-sm tracking-wider relative group py-3 flex items-center ${
          isOpen ? 'text-purple-400' : ''
        }`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onKeyDown={handleKeyDown}
      >
        <span className="uppercase font-medium">Services</span>
        <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        <span className={`absolute -bottom-1 left-0 right-0 mx-auto h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 ${
          isOpen ? 'w-full' : 'w-0 group-hover:w-full'
        }`}></span>
      </button>

      {/* Mega Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-[#1A1F2C]/95 backdrop-blur-md border border-purple-500/30 shadow-2xl shadow-purple-500/20 rounded-lg min-w-[800px] max-w-[1000px] p-6 z-50">
          <nav aria-label="Services mega menu" className="flex">
            {/* Left side: Service groups */}
            <div className="flex-1 grid grid-cols-2 gap-6 pr-6">
              {groupedServices.map(group => (
                <div key={group.name} className="space-y-3">
                  <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">
                    {GROUP_LABELS[group.name as ServiceGroup][locale]}
                  </h3>
                  
                  <ul className="space-y-2">
                    {group.services.map(service => (
                      <li key={service.id}>
                        <Link
                          to={service.path(locale)}
                          onClick={() => handleServiceClick(service)}
                          onMouseEnter={() => handleServiceHover(service)}
                          className="group flex items-center px-3 py-2 text-sm text-gray-200 hover:text-purple-400 hover:bg-purple-900/20 rounded-md transition-colors"
                        >
                          <ServiceIcon iconName={service.icon} />
                          <span className="ml-3 flex-1">{service.label[locale]}</span>
                          
                          {/* Badges */}
                          {service.badge && (
                            <Badge 
                              variant="secondary" 
                              className={`ml-2 text-xs ${
                                service.badge === 'NEW' ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'
                              }`}
                            >
                              {service.badge}
                            </Badge>
                          )}
                          
                          {/* Children indicator */}
                          {service.children && (
                            <ChevronRight className="w-3 h-3 ml-2 opacity-50" />
                          )}
                        </Link>
                        
                        {/* Sub-services */}
                        {service.children && (
                          <ul className="ml-6 mt-1 space-y-1">
                            {service.children.map(child => (
                              <li key={child.id}>
                                <Link
                                  to={child.path(locale)}
                                  onClick={() => handleServiceClick(service)}
                                  className="block px-3 py-1 text-xs text-gray-400 hover:text-purple-300 transition-colors"
                                >
                                  {child.label[locale]}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Right side: City quick links or bundle promo */}
            <div className="w-80 pl-6 border-l border-border/30">
              {hoveredService?.cityQuickLinks ? (
                <CityQuickLinks
                  serviceId={hoveredService.id}
                  servicePathFactory={hoveredService.path}
                  onCityClick={() => onToggle(false)}
                />
              ) : (
                <div className="space-y-4">
                  {/* Bundle promo */}
                  <div className="p-4 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30">
                    <h4 className="font-semibold text-purple-300 mb-2">
                      {t.bundlePromo}
                    </h4>
                    <p className="text-sm text-gray-300 mb-3">
                      Website, SEO & Analytics
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-white">
                        {t.bundlePrice}
                      </span>
                      <Link
                        to={`/${locale}/bundles/`}
                        className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded transition-colors"
                        onClick={() => onToggle(false)}
                      >
                        Details
                      </Link>
                    </div>
                  </div>

                  {/* Quick access */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-300">Quick Access</h4>
                    <Link
                      to={`/${locale}/calculator/`}
                      className="block px-3 py-2 text-sm text-gray-300 hover:text-purple-400 hover:bg-purple-900/20 rounded transition-colors"
                      onClick={() => onToggle(false)}
                    >
                      💰 Cost Calculator
                    </Link>
                    <Link
                      to={`/${locale}/case-studies/`}
                      className="block px-3 py-2 text-sm text-gray-300 hover:text-purple-400 hover:bg-purple-900/20 rounded transition-colors"
                      onClick={() => onToggle(false)}
                    >
                      📈 Case Studies
                    </Link>
                    <Link
                      to={`/${locale}/contact/`}
                      className="block px-3 py-2 text-sm text-gray-300 hover:text-purple-400 hover:bg-purple-900/20 rounded transition-colors"
                      onClick={() => onToggle(false)}
                    >
                      📞 Free Consultation
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </div>
  );
};

export default DesktopServicesMegaMenu;